import React, { Fragment } from 'react';

import { Grid, Link, Paper, Typography, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import ZendeskLink from 'src/components/Zendesk/ZendeskLink';
import ConfigContext from 'src/context/config';
import useNonNullContext from 'src/hooks/useNonNullContext';

interface Props {
  // prop used to determine if the cart sticky footer is rendering, to add some margin at the bottom so the <FunnelFooter> is not blocked
  isShowingCart: boolean;
}
const FunnelFooter: React.FC<Props> = ({ isShowingCart }) => {
  const configContext = useNonNullContext(ConfigContext);

  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const backgroundColor = theme.palette.grey[900];
  const textColor = theme.palette.grey[300];
  const accentTextColor = theme.palette.common.white;
  const watermarkColor = theme.palette.grey[600];

  // fall back to undefined for backwards compatibility
  const { termsOfUse, privacyPolicy } = configContext.globalLinks ?? {
    termsOfUse: undefined,
    privacyPolicy: undefined,
  };

  return (
    <Paper
      sx={{
        backgroundColor,
        color: textColor,
      }}
      square
    >
      {isSmallScreen ? (
        <Grid
          container
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem 1rem',
            margin: 'auto',
            marginBottom: isShowingCart ? '3.5rem' : undefined,

            ['.watermark']: {
              padding: '2rem 0',
            },
          }}
        >
          <Grid
            sx={{
              marginBottom: '0.5rem',
            }}
          >
            <Typography>Need more information?</Typography>
          </Grid>
          <Grid
            sx={{
              marginBottom: '0.5rem',
            }}
          >
            <ZendeskLink textColor={theme.palette.grey[300]} />
          </Grid>
          <Grid
            sx={{
              marginBottom: '0.5rem',
            }}
          >
            <Typography variant="subtitle1" sx={{ color: watermarkColor }}>
              {configContext.footer.watermark || (
                <Fragment>&copy; {DateTime.now().year}</Fragment>
              )}
            </Typography>
          </Grid>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Link
              href={termsOfUse.url}
              target={termsOfUse.target}
              underline="hover"
              sx={{ marginRight: '0.5rem' }}
            >
              <Typography variant="body2" sx={{ color: textColor }}>
                {termsOfUse.displayText}
              </Typography>
            </Link>
            <Link
              href={privacyPolicy.url}
              target={privacyPolicy.target}
              underline="hover"
            >
              <Typography variant="body2" sx={{ color: textColor }}>
                {privacyPolicy.displayText}
              </Typography>
            </Link>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          sx={{
            flexDirection: 'column',
            maxWidth: theme.breakpoints.values.contentMaxWidth,
            padding: '2rem 1rem',
            margin: 'auto',
            marginBottom: isShowingCart ? '4rem' : undefined,
            ['.watermark']: {
              padding: '2rem 0',
            },
          }}
        >
          <Grid>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Grid>
                <Grid
                  container
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'baseline',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ color: accentTextColor }}
                  >
                    Need more information?
                  </Typography>
                  <Grid
                    sx={{
                      padding: '0.25rem 0',
                    }}
                  >
                    <ZendeskLink textColor={theme.palette.grey[300]} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid
                  container
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ color: watermarkColor }}
                  >
                    {configContext.footer.watermark || (
                      <Fragment>&copy; {DateTime.now().year}</Fragment>
                    )}
                  </Typography>
                  <Grid
                    sx={{
                      padding: '0.25rem 0',
                      display: 'flex',
                      alignItems: 'baseline',
                    }}
                  >
                    {termsOfUse && (
                      <Link
                        href={termsOfUse.url}
                        target={termsOfUse.target}
                        underline="hover"
                        sx={{ marginRight: '0.5rem' }}
                      >
                        <Typography variant="body2" sx={{ color: textColor }}>
                          {termsOfUse.displayText}
                        </Typography>
                      </Link>
                    )}
                    {privacyPolicy && (
                      <Link
                        href={privacyPolicy.url}
                        target={privacyPolicy.target}
                        underline="hover"
                      >
                        <Typography variant="body2" sx={{ color: textColor }}>
                          {privacyPolicy.displayText}
                        </Typography>
                      </Link>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default FunnelFooter;
